// WebUSB ESC/POS printer service.
// Designed for Xprinter XP-58IIH and other generic ESC/POS USB thermal printers.
// Browser support: Chrome / Edge (WebUSB requires HTTPS).
//
// Windows note: WebUSB cannot claim a USB device that is already bound to a
// Windows printer driver. The user must install the WinUSB driver for the
// printer using Zadig (https://zadig.akeo.ie). On macOS / Linux no extra
// driver is required.

import logger from './logger';

// Known USB Vendor IDs for common ESC/POS thermal printer manufacturers.
// (Xprinter XP-58IIH most commonly enumerates as 0x0416 / 0x5011 or 0x28e9 / 0x0289.)
const PRINTER_FILTERS = [
  { vendorId: 0x0416 }, // Winbond Electronics (Xprinter chipset)
  { vendorId: 0x28e9 }, // GD Microelectronics (Xprinter newer models)
  { vendorId: 0x0fe6 }, // ICS Advent (some Xprinter)
  { vendorId: 0x0483 }, // STMicroelectronics
  { vendorId: 0x04b8 }, // Epson
  { vendorId: 0x0519 }, // Star Micronics
  { vendorId: 0x1504 }, // Bixolon
  { vendorId: 0x067b }, // Prolific
  { vendorId: 0x1fc9 }, // NXP
  { vendorId: 0x6868 }, // Hoin / generic Chinese
  { vendorId: 0x154f }, // SNBC
  { vendorId: 0x20d1 }, // Custom Engineering
];

const STORAGE_KEY = 'thermal_printer_paired';

let currentDevice = null;
let currentEndpoint = null;

const isSupported = () =>
  typeof navigator !== 'undefined' && !!navigator.usb;

const saveDeviceFingerprint = (device) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        vendorId: device.vendorId,
        productId: device.productId,
        productName: device.productName,
        manufacturerName: device.manufacturerName,
        serialNumber: device.serialNumber,
      })
    );
  } catch (e) {
    /* ignore */
  }
};

const clearDeviceFingerprint = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
};

export const getSavedFingerprint = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Find the first bulk OUT endpoint of the printer interface
const findBulkOut = (device) => {
  for (const conf of device.configurations || []) {
    for (const intf of conf.interfaces || []) {
      for (const alt of intf.alternates || []) {
        for (const ep of alt.endpoints || []) {
          if (ep.direction === 'out' && ep.type === 'bulk') {
            return {
              configurationValue: conf.configurationValue,
              interfaceNumber: intf.interfaceNumber,
              alternateSetting: alt.alternateSetting,
              endpointNumber: ep.endpointNumber,
            };
          }
        }
      }
    }
  }
  return null;
};

const openDevice = async (device) => {
  if (!device.opened) {
    await device.open();
  }
  if (device.configuration === null) {
    await device.selectConfiguration(1);
  }
  const ep = findBulkOut(device);
  if (!ep) {
    throw new Error('La impresora no expone un endpoint de impresión USB válido.');
  }
  try {
    await device.claimInterface(ep.interfaceNumber);
  } catch (err) {
    // Already claimed or in use — surface a clearer error
    const msg = String(err?.message || err);
    if (msg.toLowerCase().includes('access denied') || msg.toLowerCase().includes('in use')) {
      throw new Error(
        'No se pudo reclamar la impresora (puede estar siendo usada por el sistema). En Windows instala el driver WinUSB con Zadig.'
      );
    }
    throw err;
  }
  if (ep.alternateSetting !== 0) {
    await device.selectAlternateInterface(ep.interfaceNumber, ep.alternateSetting);
  }
  currentDevice = device;
  currentEndpoint = ep;
  return ep;
};

/**
 * Prompt the user (via the browser permission dialog) to pick the printer.
 * Must be invoked from a user-initiated event (click).
 */
export const pairPrinter = async () => {
  if (!isSupported()) {
    throw new Error('WebUSB no está disponible. Usa Chrome o Edge sobre HTTPS.');
  }
  const device = await navigator.usb.requestDevice({ filters: PRINTER_FILTERS });
  await openDevice(device);
  saveDeviceFingerprint(device);
  return {
    vendorId: device.vendorId,
    productId: device.productId,
    productName: device.productName,
    manufacturerName: device.manufacturerName,
  };
};

/**
 * Try to reconnect silently to a previously paired printer.
 * Returns the device info or null if not found.
 */
export const reconnectPrinter = async () => {
  if (!isSupported()) return null;
  const fp = getSavedFingerprint();
  if (!fp) return null;
  try {
    const devices = await navigator.usb.getDevices();
    const match = devices.find(
      (d) =>
        d.vendorId === fp.vendorId &&
        d.productId === fp.productId &&
        (fp.serialNumber ? d.serialNumber === fp.serialNumber : true)
    );
    if (!match) return null;
    await openDevice(match);
    return {
      vendorId: match.vendorId,
      productId: match.productId,
      productName: match.productName,
      manufacturerName: match.manufacturerName,
    };
  } catch (err) {
    logger.error('reconnectPrinter failed:', err);
    return null;
  }
};

export const isConnected = () => !!currentDevice && !!currentEndpoint && currentDevice.opened;

export const disconnectPrinter = async () => {
  try {
    if (currentDevice?.opened) {
      try {
        if (currentEndpoint) {
          await currentDevice.releaseInterface(currentEndpoint.interfaceNumber);
        }
      } catch (e) { /* ignore */ }
      await currentDevice.close();
    }
  } catch (err) {
    logger.error('disconnectPrinter:', err);
  } finally {
    currentDevice = null;
    currentEndpoint = null;
    clearDeviceFingerprint();
  }
};

/**
 * Send raw bytes to the printer.
 */
export const printRaw = async (bytes) => {
  if (!isConnected()) {
    // Try to silently reconnect once
    const r = await reconnectPrinter();
    if (!r) {
      throw new Error('Impresora no conectada. Ve a Configuración → Impresora para conectarla.');
    }
  }
  // Some firmwares need chunked transfers (max 64 bytes per packet)
  const CHUNK = 1024;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    const res = await currentDevice.transferOut(currentEndpoint.endpointNumber, slice);
    if (res.status !== 'ok') {
      throw new Error(`Error de transferencia USB: ${res.status}`);
    }
  }
};

export default {
  isSupported,
  pairPrinter,
  reconnectPrinter,
  disconnectPrinter,
  isConnected,
  printRaw,
  getSavedFingerprint,
};
